
import { defineComponent } from "vue";
import { useLayerStore } from "@/stores/ssaStore/layer";
import { useActiveStore } from "@/stores/ssaStore/active";
import { useSsaStore } from "@/stores/ssaStore";
import { useSaveStateStore } from "@/stores/ssaStore/saveState";
import ColorPicker from "./colorPicker.vue";
import { storeToRefs } from "pinia";
import { useDisplayPaneStore } from "../../../stores/ssaStore/displayPane";
import { useHierarchyStore } from "@/stores/hierarchyStore";
import { useFiltersStore } from "@/stores/ssaStore/filters";
import _ from "lodash";
import { hexToRgbArray, rgbArrayToHex } from "@/utils/ColorUtils";

// Not needed - delete after merge
// const lastVals = {} as any;

/* ===============================
 * Locally used types
 * ===============================*/

/** The IndexedColors component displays a list of these objects */
interface DiscreteColorEntry {
	annotation: string;
	label: string;
	hex: string;
	rgb: number[];
	opacity: number;
}

function compareDiscreteColorEntries(a: DiscreteColorEntry, b: DiscreteColorEntry): boolean {
	if (
		a.annotation !== b.annotation ||
		a.label !== b.label ||
		a.hex !== b.hex ||
		// comparing hex should be sufficient, don't do rgb
		// as then we need to worry about precision
		a.opacity !== b.opacity
	) {
		return false;
	}

	return true;
}

function arraysAreEqual(arrA: DiscreteColorEntry[], arrB: DiscreteColorEntry[]): boolean {
	if (arrA.length !== arrB.length) return false;

	return arrA.every((item, idx) => compareDiscreteColorEntries(item, arrB[idx]));
}

export default defineComponent({
	name: "SSAIndexedColorEditorWidget",
	props: {
		isLoading: {
			type: Boolean,
			default: false
		},
		isBlastdog: {
			type: Boolean,
			default: false
		},
		colorByFieldProp: {
			type: Object
		},
		useGASAttributesProp: {
			type: Boolean,
			default: false
		},
		componentLayerName: {
			type: String,
			default: ""
		}
	},
	components: {
		ColorPicker
	},
	setup() {
		const ssaStore = useSsaStore();
		const saveStateStore = useSaveStateStore();
		const { SSA_LAYER_STATES: layerStates } = storeToRefs(useLayerStore());
		const { SSA_ACTIVE_DATASET: activeDataSet } = storeToRefs(useActiveStore());
		const {
			SSA_ACTIVE_LAYER: activeLayer,
			SSA_ACTIVE_VIEW: activeView,
			SSA_ACTIVE_REPRESENTATION_STATE: activeRepresentationState
		} = storeToRefs(useActiveStore());
		const hierarchyStore = useHierarchyStore();

		const filtersStore = useFiltersStore();
		return {
			layerStates,
			activeLayer,
			activeView,
			activeRepresentationState,
			updateColorBy: ssaStore.SSA_COLOR_BY_UPDATE,
			pushOpacity: ssaStore.SSA_CATEGORY_OPACITY_UPDATE,
			hierarchyStore,
			activeDataSet,
			saveStateStore,
			filtersStore
		};
	},
	data() {
		return {
			colorPickerIndex: 0,
			colorOpen: false,
			// localOpacities: null,  // DELETE AFTER MERGE
			colorOpenListener: null as any,
			isAllCategoriesVisible: true,
			discreteColorsList: [] as DiscreteColorEntry[],
			/**
			 * Each layer has a cache of color entries containing a string array usedHex
			 * and a map of annotation keys to DiscreteColorEntry objects
			 */
			cachedColorEntries: {} as any, // Added for mpd-4075 - surfaces in same dataset
			loadStateTracking: null
		};
	},
	mounted() {
		// Sometimes we get a representation state update on selectDataset (and this component is mounted)
		// and sometimes we don't (e.g. when selecting a non-active item in the hierarchy)
		this.$nextTick(async () => {
			// Try to detect the case where we have switched to a dataset with this component open,
			// and we haven't had a representation state update
			if (this.indexedColors?.length > 0 && this.discreteColorsList.length === 0) {
				// console.log("Recalculating discrete colors list from remote colors");
				this.discreteColorsList = this.calculateDiscreteColors();
			}
		});
	},
	watch: {
		discreteColors(v) {
			this.loadStateTracking = this.saveStateStore.SSA_ACTIVE_SAVE_STATE;
			// console.log("discreteColors changed! length now ", v?.length);
		},
		// cachedAnnotations: {
		// 	deep: true,
		// 	handler(newVal) {
		// 		console.log("cachedAnnotations changed!", newVal);
		// 	}
		// },
		colorByFieldProp(v) {
			// console.log("colorByFieldProp changed! ", v);
			if (this.activeLayer) {
				// Clear the cache
				this.cachedColorEntries[this.activeLayer] = null;
			}
		},
		// useGASAttributesProp(v) {
		// 	console.log("🍓 useGASAttributesProp changed! ", v);
		// },
		activeLayer(v) {
			// this.localOpacities = null;

			if (v) {
				const selectedLayer = useDisplayPaneStore().DISPLAY_STATES[this.activeView][
					useActiveStore().SSA_ACTIVE_DATASET.id
				].find((layer: any) => {
					return v.includes(layer.fileName);
				});

				if (selectedLayer && selectedLayer.selected) {
					this.$nextTick(async () => {
						// console.log("🐢 activeLayer changed - refreshing categories for", v);
						await this.refreshCategories();
					});
				}
			}
		},
		activeView() {
			// this.localOpacities = null;
		},
		layerStates(val, oldval) {
			const layer = Object.keys(val[this.activeView]).find(
				(layer) => layer == this.activeLayer
			);

			if (!layer) {
				return;
			}

			if (!oldval || Object.keys(oldval[this.activeView][layer] ?? []).length === 0) {
				// console.log("🐢 layerStates changed - refreshing categories ", val);
				this.refreshCategories();
			} else if (oldval[this.activeView][layer] != null) {
				const old_loaded_pieces = JSON.parse(
					JSON.stringify(oldval[this.activeView][layer]["selection"]["names"])
				);
				const new_loaded_pieces = JSON.parse(
					JSON.stringify(val[this.activeView][layer]["selection"]["names"])
				);
				if (_.isEqual(old_loaded_pieces, new_loaded_pieces) == false) {
					//console.log("refreshing",old_loaded_pieces,new_loaded_pieces)
					// console.log("🐢 loadedPieces changed - refreshing categories ", val);
					this.refreshCategories();
				}
			}
		},
		remoteColorByState(s) {
			// Clear local value when server catch up
			// if (this.localOpacities && s && s.indexedOpacities) {
			// 	const a = s.indexedOpacities.join(",");
			// 	const b = this.localOpacities.join(",");
			// 	if (a === b) {
			// 		this.localOpacities = null;
			// 	}
			// }

			if (s && s.indexedOpacities) {
				this.isAllCategoriesVisible = false;
				for (let i = 0; i < s.indexedOpacities.length; i++) {
					if (s.indexedOpacities[i]) {
						this.isAllCategoriesVisible = true;
						break;
					}
				}
			}

			if (
				s.annotations == null ||
				(s.annotations.length == 0 && s.indexedColors?.length > 0)
			) {
				// mismatch between indexed colors and annotations
				console.warn("🚨 Indexed colors and annotations mismatch");
				this.refreshCategories();
			} else if (this.activeLayer) {
				// Recalculate the list of discrete colors
				this.discreteColorsList = this.calculateDiscreteColors();
			} else {
				// When unchecking a hierarchy item we sometimes get an update with empty annotations
				// and no active layer.  In this case we don't want to update the cache or push to server
				// console.log("Ignoring update, no active layer");
			}
		}
	},
	computed: {
		isUsingColorAttributes() {
			const activeDataSet = useActiveStore().SSA_ACTIVE_DATASET;
			// Do we have color attributes in the attributes map?
			const hasColorAttributes =
				activeDataSet &&
				activeDataSet.attributesMap &&
				activeDataSet.attributesMap.color &&
				activeDataSet.attributesMap.color.attributes &&
				activeDataSet.attributesMap.color.attributes.length > 0;
			let useAtts = hasColorAttributes;
			// If we are an ioGAS dataset, are we currently showing ioGAS attributes?
			if (hasColorAttributes && this.isGASDataset) {
				useAtts = this.useGASAttributesProp;
			}
			return useAtts;
		},
		isGASDataset() {
			// Props aren't always updated early enough to use, so use the layer name
			return this.activeLayer && this.activeLayer.endsWith(".gas");
		},
		datasetColorMap() {
			const activeDataSet = useActiveStore().SSA_ACTIVE_DATASET;

			if (this.isUsingColorAttributes) {
				// console.log("🌸 In datasetColorMap: gas file = ", this.isGASDataset);
				return activeDataSet.attributesMap.color.attributes.reduce(
					(acc: any, curr: any, index: number) => {
						let parsedRgbArray;
						let hex = curr.color;

						if (curr.color.startsWith("#")) {
							parsedRgbArray = hexToRgbArray(hex);
						} else {
							// TODO: remove this code when mplib changes are in
							// This is a temporary fix for the color attribute being in
							// the wrong format [r, g, b] instead of a hex string
							parsedRgbArray = JSON.parse(curr.color);

							// We have to do a conversion because the rgb colors
							// from the attributes are in 0-255 range while
							// the paraview ones are in the 0-1 range
							parsedRgbArray = parsedRgbArray.map((i: number) => {
								return i / 255;
							});

							hex = rgbArrayToHex(parsedRgbArray);
						}

						// In the case of ioGAS datasets, the annotation is the index of the attribute
						// in the attribute list, and the label is the name of the attribute
						const annotationKey = this.isGASDataset ? index.toString() : curr.name;

						acc[annotationKey] = {
							annotation: annotationKey,
							label: curr.name,
							hex,
							rgb: parsedRgbArray,
							opacity: 1,
							show: true
						};

						return acc;
					},
					{}
				);
			}

			return {};
		},
		activeColorComputed() {
			if (this.discreteColors[this.colorPickerIndex]?.hex === undefined)
				console.log("color picker color undefined");
			const ret = { color: this.discreteColors[this.colorPickerIndex]?.hex };
			return ret;
		},

		remoteColorByState() {
			const firstArg = {
				annotations: [],
				indexedColors: []
			};

			return Object.assign(
				firstArg,
				this.activeRepresentationState && this.activeRepresentationState.coloring,
				{
					layerName: this.activeLayer,
					viewId: this.activeView
				}
			);
		},
		annotations() {
			// These come in pairs.  The first one is the categorical
			// value that Paraview looks for, and the second one is the
			// label for it shown in the UI
			return this.remoteColorByState.annotations;
		},
		indexedColors() {
			// These come in triplets of RGB values	in the [0,1] range
			return this.remoteColorByState.indexedColors;
		},
		indexedOpacities() {
			return /* this.localOpacities || */ this.remoteColorByState.indexedOpacities;
		},
		// This is the list of discrete colors that we will show in the UI -
		// it is recalculated each time we get a remoteColorByState from Paraview
		discreteColors() {
			return this.discreteColorsList;
		}
	},
	methods: {
		/**
		 * Rebuild the list of discrete color entries we will display in the UI,
		 * after receiving a representation state update from Paraview.
		 */
		calculateDiscreteColors(): DiscreteColorEntry[] {
			if (this.activeLayer !== this.componentLayerName) {
				return this.discreteColorsList;
			}
			// The list of color entries that will be returned from this method
			const list = [] as DiscreteColorEntry[];
			// Flag to indicate we're leaving out some categories
			let pushAnnotationsToServer = false;

			// Annotations come in pairs.
			// First is the actual value that Paraview will use for lookup.
			// Second is the label will show in the GUI.  For surfaces they will be the same.
			// For ioGAS datasets the first will be the index of the attribute in the attribute list,
			// and the second will be the name of the attribute - i.e. NOT THE SAME
			const annotationCopy = [...this.annotations];
			const numAnnotations = annotationCopy.length / 2;
			// The key is used as an index to both the cache and the datasetColorMap
			const annotationKeys = annotationCopy.filter((entry, index) => index % 2 === 0);

			// console.log(
			// 	"🐋 ###### ENTRY discreteColors() for layer ",
			// 	this.activeLayer,
			// 	"colorBy ",
			// 	this.colorByFieldProp?.label,
			// 	": ",
			// 	annotationKeys
			// );

			// Record the state of colors and annotations pushed from Paraview
			const remoteList = [] as DiscreteColorEntry[];
			for (let i = 0; i < numAnnotations; i++) {
				const rgb = this.indexedColors.slice(i * 3, i * 3 + 3);
				const hex = rgbArrayToHex(rgb);
				const colorEntry = {
					annotation: annotationCopy[i * 2], // annotation key
					label: annotationCopy[i * 2 + 1], // annotation label
					rgb,
					hex,
					opacity: this.indexedOpacities[i],
					show: true
				} as DiscreteColorEntry;
				// Check if we need to auto-hide this entry because it's a NaN
				const includeEntry =
					colorEntry.annotation.length > 0 &&
					colorEntry.annotation.toLowerCase() !== "nan";

				// Don't even include it in our starting list
				if (includeEntry) {
					remoteList.push(colorEntry);
				} else {
					// ...but tell the server to leave out this category
					pushAnnotationsToServer = true;
				}
			}

			// console.log(
			// 	"    🐋 remote ",
			// 	remoteList,
			// 	" cached local ",
			// 	this.cachedColorEntries[this.activeLayer]
			// );

			// Record the keys of the objects that are already in the cache
			const annotationsWithExistingColors = this.clearUnusedFromCache(annotationKeys);

			const loadingState = this.saveStateStore.SSA_ACTIVE_SAVE_STATE;

			if (this.isUsingColorAttributes) {
				if (loadingState && !this.loadStateTracking) {
					// When first loading a saved state, just use the list from the server.
					// We will replace the entries with the ones from the saved state later on.
					list.push(...remoteList);
				} else {
					// We have colorAttributes (DXF or gas file)
					remoteList.forEach((remoteEntry) => {
						const annotationKey = remoteEntry.annotation;

						let cached = null as DiscreteColorEntry | null;
						if (annotationsWithExistingColors?.includes(annotationKey)) {
							// This one's cached: use the color from the cache, as
							// the user may have changed it from the initial datasetColorMap value.
							cached = this.cachedColorEntries[this.activeLayer][annotationKey];
						}

						if (this.datasetColorMap[annotationKey]) {
							// Overwrite colors and labels with values from the attribute map if not cached
							// (Currently we don't support opacity in the attribute map, but we could)
							const colorEntry = {
								annotation: annotationKey,
								label: this.datasetColorMap[annotationKey].label,
								rgb: cached?.rgb || this.datasetColorMap[annotationKey].rgb,
								hex: cached?.hex || this.datasetColorMap[annotationKey].hex,
								// User may have toggled opacity, so we need to use the remote opacity
								opacity: remoteEntry.opacity
							} as DiscreteColorEntry;

							list.push(colorEntry);

							// If we haven't loaded labels: send to server.
							if (remoteEntry.label != colorEntry.label) {
								pushAnnotationsToServer = true;
							}
						} else {
							console.warn(
								`❌ Could not find color for annotation ${annotationKey} in datasetColorMap`
							);
						}
						// }
					});
				}
			} else if (this.indexedColors.length !== 0) {
				// No color attributes; use indexed colors - e.g. Drillholes, or non-ioGAS points datasets
				// However, we need to make sure that any that are already in the list
				// keep the colors they have already been assigned

				// Store list of all colors from remote, with their original indices
				const allColors = remoteList.map((entry, index) => ({
					hex: entry.hex,
					idx: index // store the original index so we can get the rgb values
				}));

				// The list of colors that are already in use, in the cache
				const occupiedColors = this.cachedColorEntries[this.activeLayer]
					? [...this.cachedColorEntries[this.activeLayer]["usedHex"]]
					: [];

				// The list of colors NOT in use, with their original indices into the remoteList
				const remainingColors = allColors.filter(
					(item) => !occupiedColors.includes(item.hex)
				);

				remoteList.forEach((remoteEntry) => {
					const annotationKey = remoteEntry.annotation;

					if (annotationsWithExistingColors?.includes(annotationKey)) {
						// This one's cached: use the colors and annotations from the cache.
						// Its hex code has already been put in the occupiedColors.
						// But, because the user may have toggled opacity, we need to use the remote opacity
						const cachedItem = this.cachedColorEntries[this.activeLayer][annotationKey];
						const newItem = {
							...cachedItem,
							opacity: remoteEntry.opacity
						};
						list.push(newItem);
						return;
					}

					let hex: string | undefined;
					let rgb: number[] | undefined;

					hex = remoteEntry.hex; // start with the original hex
					rgb = remoteEntry.rgb; // and rgb

					// If the remote color is already used, find a new color from the unoccupied ones
					// Check if originalHex is already occupied
					if (occupiedColors.includes(hex)) {
						// If so: use the first color from remainingColors
						const availableColor = remainingColors.shift();
						if (!availableColor) {
							console.warn("🚨 No more remaining colors available!");
						} else {
							hex = availableColor.hex;
							// find the corresponding entry in indexedColors
							rgb = this.indexedColors.slice(
								availableColor.idx * 3,
								availableColor.idx * 3 + 3
							);
						}
					} // else just use the original color

					if (hex) {
						// Save this one to the list of occupied colors
						occupiedColors.push(hex);
						const colorEntry = {
							annotation: remoteEntry.annotation,
							label: remoteEntry.label,
							rgb,
							hex,
							opacity: remoteEntry.opacity
						} as DiscreteColorEntry;

						list.push(colorEntry);
					}
				});
			}

			// Commenting out this code: no one knows what it is here for.
			// Will delete post-merge so that there is a record of it.
			// if (list.length === 0) {
			// 	if (
			// 		(lastVals["icolors"] != JSON.stringify(this.indexedColors) &&
			// 			JSON.stringify(this.indexedColors) != "[]") || // no need to refresh on empty values (and this prevents surfaces from resetting to white after first colorBy call)
			// 		(lastVals["annot"] != JSON.stringify(annotationCopy) &&
			// 			JSON.stringify(annotationCopy) != "[]") ||
			// 		(lastVals["iopacity"] != JSON.stringify(this.indexedOpacities) &&
			// 			JSON.stringify(this.indexedOpacities) != "[]")
			// 	) {
			// 		this.refreshCategories();
			// 		lastVals["icolors"] = JSON.stringify(this.indexedColors);
			// 		lastVals["annot"] = JSON.stringify(annotationCopy);
			// 		lastVals["iopacity"] = JSON.stringify(this.indexedOpacities);
			// 	}
			// }

			// Put everything that's now in the list into the cache for next time
			this.rebuildCacheFromList(list);

			// Is our computed list in any way different to the one we got from the pvw server?
			// If so, send the new list to the server
			const hasListChanged = pushAnnotationsToServer || !arraysAreEqual(list, remoteList);
			if (hasListChanged) {
				const indexedColors = [] as number[];
				const annotations = [] as string[];
				const indexedOpacities: number[] = [];

				list.forEach((entry) => {
					entry.rgb.forEach((rgbComponent) => {
						indexedColors.push(rgbComponent); // array triple
					});
					annotations.push(entry.annotation);
					annotations.push(entry.label);
					indexedOpacities.push(entry.opacity);
				});

				// console.log("    🚀 UPDATING COLORBY: sending indexedColors ", indexedColors);
				// // console.log("    🚀                 : sending annotations ", listAnnotations);

				// N.B.!! for some reason if you send the annotations without the opacities,
				// Paraview gets VERY confused
				const { layerName, viewId, field } = this.remoteColorByState;

				this.updateColorBy({
					layerName,
					viewId,
					field,
					indexedColors,
					annotations,
					indexedOpacities
				});
			}

			// console.log(
			// 	"🐋 ===== discreteColors (",
			// 	hasListChanged ? "changed" : "no change",
			// 	") returning ",
			// 	list
			// );
			return list;
		},
		/**
		 * Remove any entries from the cache that are not currently selected, and return the list
		 * of keys for annotations that are currently selected and have existing colors in the cache
		 * @param annotationKeys entries that are currently selected
		 */
		clearUnusedFromCache(annotationKeys: string[]): string[] {
			let annotationsWithExistingColors: string[] = [];
			if (this.cachedColorEntries[this.activeLayer]) {
				const copyOfCachedAnnotations = { ...this.cachedColorEntries[this.activeLayer] }; // Copy to delete he usedHex key without modifying original object
				delete copyOfCachedAnnotations["usedHex"];

				// Cache annotations so that colours don't jump around
				// when selecting surfaces in the same dataset
				const cachedAnnotationKeys = Object.keys(copyOfCachedAnnotations); // Get the annotation names
				annotationsWithExistingColors = cachedAnnotationKeys
					.filter((annotation) => {
						// Annotations that ARE currently selected
						return annotationKeys.includes(annotation);
					})
					.sort();

				// console.log(
				// 	"    🐋 current annotationsWithExistingColors ",
				// 	annotationsWithExistingColors
				// );

				// Remove anything from the cache that isn't currently switched on
				cachedAnnotationKeys
					.filter((annotation) => {
						// Annotations not currently selected
						return !annotationKeys.includes(annotation);
					})
					.forEach((unselected) => {
						const colorUsedByUnselected =
							this.cachedColorEntries[this.activeLayer][unselected].hex;
						const indexOfColor =
							this.cachedColorEntries[this.activeLayer]["usedHex"].indexOf(
								colorUsedByUnselected
							);

						if (indexOfColor > -1) {
							// console.log(
							// 	"    🐋 Removing unselected annotation ",
							// 	unselected,
							// 	" with color ",
							// 	colorUsedByUnselected
							// );
							this.cachedColorEntries[this.activeLayer]["usedHex"].splice(
								indexOfColor,
								1
							); // Remove the cached color and put back into the pool
						}

						delete this.cachedColorEntries[this.activeLayer][unselected]; // Delete the unselected annotatations from the cache annotations
					});
			}
			return annotationsWithExistingColors;
		},
		removeColorFromCache(annotation: string) {
			if (this.cachedColorEntries[this.activeLayer]) {
				const colorToCheck = this.cachedColorEntries[this.activeLayer][annotation].hex;
				const indexOfColor =
					this.cachedColorEntries[this.activeLayer]["usedHex"].indexOf(colorToCheck);
				if (indexOfColor > -1) {
					// Remove the cached color and put back into the pool
					this.cachedColorEntries[this.activeLayer]["usedHex"].splice(indexOfColor, 1);
				}
				delete this.cachedColorEntries[this.activeLayer][annotation];
			}
		},
		/**
		 * The list of DiscreteColorEntries has been built for the currently selected hierarchy
		 * items.  Put them into the cache to ensure that the colors are not reassigned when
		 * selecting different hierarchy items in the same dataset.
		 * @param list  the list of DiscreteColorEntry objects displayed by this component
		 */
		rebuildCacheFromList(list: DiscreteColorEntry[]) {
			list.forEach((entry) => {
				// Rebuild the saved list of currently used hex values
				if (!this.cachedColorEntries[this.activeLayer]) {
					this.cachedColorEntries[this.activeLayer] = {};
					this.cachedColorEntries[this.activeLayer]["usedHex"] = [];
				}

				if (!entry.hex) {
					// Should never happen
					console.warn("🚨 No hex value for annotation ", entry.annotation);
				} else {
					// WARNING!!! Reference copy.  If the user changes the color, it will change in the cache
					this.cachedColorEntries[this.activeLayer][entry.annotation] = entry;

					if (!this.cachedColorEntries[this.activeLayer]["usedHex"].includes(entry.hex)) {
						this.cachedColorEntries[this.activeLayer]["usedHex"].push(entry.hex);
					}
				}
			});
		},
		toggleColorOpen(idx: number, ref: any) {
			this.colorPickerIndex = idx;
			this.colorOpen = !this.colorOpen;
			const container = window.document.getElementById("pane-container") as HTMLElement;
			if (this.colorOpen) {
				this.$nextTick(() => {
					window.addEventListener(
						"mousedown",
						(this.colorOpenListener = (e: any) => {
							const [x, y] = [e.pageX, e.pageY];
							const elements = document.elementsFromPoint(x, y);
							for (let i = 0; i < elements.length; i++) {
								if (elements[i].classList.contains("color-picker")) {
									return;
								}
							}
							this.colorOpen = false;
							window.removeEventListener("mousedown", this.colorOpenListener);
						})
					);
					const wHeight = window.innerHeight;
					const componentRefs = this.$refs[ref] as HTMLElement[];
					const element = componentRefs[0];
					const { left, top } = element.getBoundingClientRect();

					const colorPickerElement = (this.$refs.colorPicker as any).$el as HTMLElement;
					const { height } = colorPickerElement.getBoundingClientRect();
					if (top > wHeight - height) {
						colorPickerElement.style.top = wHeight - height + "px";
					} else {
						colorPickerElement.style.top = top + "px";
					}

					colorPickerElement.style.left = left - 250 + "px";

					container.appendChild(colorPickerElement);
				});
			}
		},
		// Called when user updates an individual color using the color picker
		async updateColor(v: any) {
			if (v.color != null && v.rgba != null) {
				const { layerName, viewId, field, indexedColors } = this.remoteColorByState;

				const discreteColorsEntry = this.discreteColors[this.colorPickerIndex];
				const key = discreteColorsEntry.annotation;

				if (
					this.cachedColorEntries[this.activeLayer] &&
					this.cachedColorEntries[this.activeLayer][key]
				) {
					const colorToCheck = this.cachedColorEntries[this.activeLayer][key].hex;
					const indexOfColor =
						this.cachedColorEntries[this.activeLayer]["usedHex"].indexOf(colorToCheck);
					if (indexOfColor > -1) {
						// Remove the cached color and put back into the pool
						this.cachedColorEntries[this.activeLayer]["usedHex"].splice(
							indexOfColor,
							1
						);
						// Don't remove entry from the cache - it breaks gas files but shouldn't!
						// TODO Investigate while fixing MPD-4477
					}
				}

				const { r, g, b } = v.rgba;

				// Indexed Colors contain an array of all current colors in rgb groups of three,
				// so we need to just replace the color for where the index of the annotation is
				const index = this.colorPickerIndex * 3;
				indexedColors[index + 0] = r / 255;
				indexedColors[index + 1] = g / 255;
				indexedColors[index + 2] = b / 255;
				const replacementRgbForAnnotation = indexedColors.slice(index, index + 3);

				// WARNING!!! Cache contains reference copies so this will change the cached copy also.
				// Directly modifying the computed property here is not recommended - likely related to mpd-4491
				discreteColorsEntry.hex = v.color.toLowerCase();
				discreteColorsEntry.rgb = replacementRgbForAnnotation;

				this.updateColorBy({
					layerName,
					viewId,
					field,
					indexedColors
				});
				//this.colorOpen = !this.colorOpen;
			}
		},
		toggleAllOpacity() {
			const { layerName, viewId, field } = this.remoteColorByState;
			let { indexedOpacities } = this.remoteColorByState;

			this.isAllCategoriesVisible = !this.isAllCategoriesVisible;

			if (this.isAllCategoriesVisible) {
				indexedOpacities = indexedOpacities.map(() => {
					return 1;
				});
			} else {
				indexedOpacities = indexedOpacities.map(() => {
					return 0;
				});
			}

			// this.localOpacities = indexedOpacities.slice();
			this.updateColorBy({
				layerName,
				viewId,
				field,
				indexedOpacities
			});
		},
		toggleOpacity(idx: number) {
			const { layerName, viewId, field, indexedOpacities } = this.remoteColorByState;
			indexedOpacities[idx] = indexedOpacities[idx] ? 0 : 1;

			// this.localOpacities = indexedOpacities.slice();
			this.updateColorBy({
				layerName,
				viewId,
				field,
				indexedOpacities
			});
		},
		async refreshCategories() {
			const { layerName, viewId, field, colorType } = this.remoteColorByState;

			if (field != null) {
				// mpd-4161 - Threshold Filters prevent fetching of category list for BLASTDOG datasets
				if (this.isBlastdog) {
					for (const filter of this.filtersStore.SSA_ALL_ACTIVE_FILTERS) {
						if (
							colorType === "Categorical" &&
							filter.layerName === layerName &&
							(filter.ThresholdRange[0] > filter.absMin ||
								filter.ThresholdRange[1] < filter.absMax)
						) {
							this.filtersStore.SSA_TEMP_DISABLE_FILTERS(layerName);
							break;
						}
					}
				}

				// console.log("🐢🐢----  refreshCategories: START ", layerName, " ------");
				await this.updateColorBy({
					layerName,
					viewId,
					field,
					forceCategoricalComputation: false // need to reset previous call
				});
				await this.updateColorBy({
					layerName,
					viewId,
					field,
					forceCategoricalComputation: true
				});

				// console.log("🐢🐢----  refreshCategories: END ", layerName, " ------");
			}
		}
	}
});
